import type { TaskListResponse } from '../../services/api/types.gen';
import { closedEpisodeCopy } from '../../utils/closedEpisodeLock';
import type { UnclaimedClinicianVariant } from '../clinician/unclaimedClinicianCopy';

/**
 * What the task table says about an unclaimed imported assignee.
 *
 * The row already knows whether its picker is on screen. This turns that
 * answer, and the reason behind it, into the marker's variant and the lines
 * it appends — so the popover offers a reassignment only where the row would
 * accept one, and otherwise says why it will not.
 *
 * Reasons are checked in the order the row locks: a deleted row first, then
 * a closed period, then a status lock, then the Episode Manager permission,
 * then a communication whose assignee lives on its own panel.
 */
export interface UnclaimedAssigneeInput {
  taskType: TaskListResponse['task_type'];
  /** The row renders its assignee picker. */
  canEditInline: boolean;
  deleted: boolean;
  /** `episode_closed` on the row, regardless of who is looking. */
  episodeClosed: boolean;
  canAmendClosedEpisode: boolean;
  lockedByStatus: boolean;
  episodeManagerDenied: boolean;
}

export interface UnclaimedAssigneeMarker {
  variant: Extract<UnclaimedClinicianVariant, 'reassignable' | 'locked'>;
  detailLines: string[];
}

const LOCKED_NOUN: Record<TaskListResponse['task_type'], string> = {
  visit: 'visit',
  order: 'order',
  misc_task: 'task',
  patient_communication: 'communication',
};

export function unclaimedAssigneeMarker(
  input: UnclaimedAssigneeInput
): UnclaimedAssigneeMarker {
  if (input.canEditInline) {
    const closed = input.episodeClosed && input.canAmendClosedEpisode;
    const amend = closedEpisodeCopy(true);
    return {
      variant: 'reassignable',
      detailLines: closed ? [amend.headline, amend.detail] : [],
    };
  }

  const noun = LOCKED_NOUN[input.taskType];
  if (input.deleted) {
    return {
      variant: 'locked',
      detailLines: [
        `This ${noun} was deleted, so its assignee stays as recorded.`,
      ],
    };
  }
  if (input.episodeClosed && !input.canAmendClosedEpisode) {
    const closed = closedEpisodeCopy(false);
    return { variant: 'locked', detailLines: [closed.headline, closed.detail] };
  }
  if (input.lockedByStatus) {
    return {
      variant: 'locked',
      detailLines: [
        `This ${noun} is locked, so its assignee stays as recorded.`,
      ],
    };
  }
  if (input.episodeManagerDenied) {
    return {
      variant: 'locked',
      detailLines: [
        'Changing the assignee needs the Episode Manager permission.',
      ],
    };
  }
  if (input.taskType === 'patient_communication') {
    return {
      variant: 'locked',
      detailLines: ['Assign this communication from its detail panel.'],
    };
  }
  return { variant: 'locked', detailLines: [] };
}
